import './ConstraintFormModal.scss';
import { ReactNode, useState } from 'react';
import { Form, Modal } from 'react-bootstrap';
import { Button } from '../Button';
import { Appearance, Design, Intent } from '../designConstants';

export const ConstraintFormMode = {
  NEW: 'new',
  COPY: 'copy'
} as const;
export type ConstraintFormModeType = (typeof ConstraintFormMode)[keyof typeof ConstraintFormMode];

interface ConstraintFormModalProps {
  mode: ConstraintFormModeType;
  // Read-only "From" field shown only in copy mode.
  sourceName?: string;
  // value -> label, rendered as <option>s in source order.
  optimizationTypeOptions: Record<string, string>;
  initialOptimizationType?: string;
  // Copying preserves the source's type; creating lets the user pick.
  disableOptimizationType?: boolean;
  getNameError: (name: string) => string | undefined;
  // Additional submit-gating beyond the name error -- e.g. "traffic needs an allocation date".
  isValid?: (name: string, optimizationType: string) => boolean;
  // Renders whatever traffic-allocation-date field this caller's domain needs (a date range, a
  // single week, or nothing) below the type selector. The caller owns that field's state itself
  // and reads it directly (via closure) inside its own onSubmit -- this component only owns
  // name/optimizationType.
  renderExtraFields?: (optimizationType: string) => ReactNode;
  onHide: () => void;
  onSubmit: (input: { name: string; optimizationType: string }) => Promise<void> | void;
}

export const ConstraintFormModal = ({
  mode,
  sourceName,
  optimizationTypeOptions,
  initialOptimizationType,
  disableOptimizationType = false,
  getNameError,
  isValid,
  renderExtraFields,
  onHide,
  onSubmit
}: ConstraintFormModalProps) => {
  const [name, setName] = useState('');
  const [optimizationType, setOptimizationType] = useState(
    initialOptimizationType ?? Object.keys(optimizationTypeOptions)[0]
  );
  const [submitting, setSubmitting] = useState(false);
  const nameError = getNameError(name);
  const canSubmit = !nameError && (isValid?.(name, optimizationType) ?? true);

  return (
    <Modal show onHide={onHide} size='lg' centered>
      <Modal.Header closeButton>
        <Modal.Title>
          {mode === ConstraintFormMode.COPY ? 'Copy Constraint' : 'New Constraint'}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className='constraint-form-modal-body'>
        {mode === ConstraintFormMode.COPY && (
          <Form.Group>
            <Form.Label>From</Form.Label>
            <Form.Control value={sourceName} readOnly />
          </Form.Group>
        )}
        <Form.Group>
          <Form.Label>Name</Form.Label>
          <Form.Control
            autoFocus
            value={name}
            isInvalid={Boolean(nameError)}
            onChange={event => setName(event.target.value.replace(/\s/g, ''))}
          />
          <Form.Control.Feedback type='invalid'>{nameError}</Form.Control.Feedback>
        </Form.Group>
        <Form.Group>
          <Form.Label>Optimization Type</Form.Label>
          <Form.Select
            value={optimizationType}
            disabled={disableOptimizationType}
            onChange={event => setOptimizationType(event.target.value)}
          >
            {Object.entries(optimizationTypeOptions).map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </Form.Select>
        </Form.Group>
        {renderExtraFields?.(optimizationType)}
      </Modal.Body>
      <Modal.Footer>
        <Button appearance={Appearance.OUTLINED} design={Design.NEUTRAL} onClick={onHide}>
          Cancel
        </Button>
        <Button
          design={Intent.SUCCESS}
          disabled={!canSubmit}
          loading={submitting}
          onClick={async () => {
            setSubmitting(true);
            try {
              await onSubmit({ name, optimizationType });
            } finally {
              setSubmitting(false);
            }
          }}
        >
          {mode === ConstraintFormMode.COPY ? 'Copy' : 'Create'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};
